import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ScrollRailItem } from '@nula/core-ui';

interface UseMessageRailItemsOptions {
  scrollElRef: React.RefObject<HTMLElement | null>;
  /** Ref to a Map of message id → DOM element, populated by the message list */
  elemsRef: React.RefObject<Map<string, HTMLElement>>;
  /** Ordered list of message ids to show as rail items */
  messageIds: string[];
  /** Display labels keyed by message id. Defaults to the id itself if absent. */
  labels?: Map<string, string>;
}

interface UseMessageRailItemsResult {
  items: ScrollRailItem[];
  activeId: string | null;
  jumpTo: (id: string) => void;
}

/**
 * useMessageRailItems — tracks which user message is active as the user scrolls,
 * and exposes a jumpTo function that scrolls it into view.
 *
 * Mirrors useHeadingAnchors but driven by a DOM-element Map instead of Lexical
 * state, so it works for any scrollable list of items (chat messages, etc.).
 */
export function useMessageRailItems({
  scrollElRef,
  elemsRef,
  messageIds,
  labels,
}: UseMessageRailItemsOptions): UseMessageRailItemsResult {
  const [activeId, setActiveId] = useState<string | null>(messageIds[0] ?? null);

  // Recompute activeId on scroll
  useEffect(() => {
    const scrollEl = scrollElRef.current;
    if (!scrollEl) return;

    const onScroll = () => {
      const scrollTop = scrollEl.scrollTop;
      const containerTop = scrollEl.getBoundingClientRect().top;
      const offset = 40;
      let current: string | null = null;

      messageIds.forEach((id) => {
        const el = elemsRef.current?.get(id);
        if (!el) return;
        const elTop = el.getBoundingClientRect().top - containerTop + scrollEl.scrollTop;
        if (elTop - offset <= scrollTop) current = id;
      });

      setActiveId(current ?? messageIds[0] ?? null);
    };

    onScroll();
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [scrollElRef, elemsRef, messageIds]);

  const jumpTo = useCallback((id: string) => {
    const el = elemsRef.current?.get(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  }, [elemsRef]);

  const items = useMemo<ScrollRailItem[]>(
    () => messageIds.map((id) => ({ id, label: labels?.get(id) ?? id })),
    [messageIds, labels],
  );

  return { items, activeId, jumpTo };
}
